import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate  = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }); };
const fmtMoney = (n) => { const v=Number(n||0); if(v>=10000000) return `₹${(v/10000000).toFixed(1)}Cr`; if(v>=100000) return `₹${(v/100000).toFixed(1)}L`; return `₹${v.toLocaleString('en-IN')}`; };
const PAGE_SIZE = 10;

// ── Status Pill ───────────────────────────────────────────────────────────────
const StatusPill = ({ status }) => {
  const map = {
    ACTIVE:    { bg:'rgba(45,95,79,0.12)',   color:'#2D5F4F', label:'Active'    },
    UPCOMING:  { bg:'rgba(245,158,11,0.12)', color:'#F59E0B', label:'Upcoming'  },
    EXPIRED:   { bg:'rgba(196,190,184,0.2)', color:'#6B6B6B', label:'Expired'   },
    CANCELLED: { bg:'rgba(232,93,74,0.12)',  color:'#E85D4A', label:'Cancelled' },
  };
  const s = map[(status||'EXPIRED').toUpperCase()] || map.EXPIRED;
  return <span style={{ display:'inline-flex', alignItems:'center', padding:'4px 10px', borderRadius:20, fontSize:10.5, fontWeight:700, letterSpacing:0.8, textTransform:'uppercase', background:s.bg, color:s.color }}>{s.label}</span>;
};

// ── Tower Pill ────────────────────────────────────────────────────────────────
const TowerPill = ({ tower }) => {
  const isA = tower === 'A';
  return <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:4, fontSize:10.5, fontWeight:700, letterSpacing:0.5, background: isA?'rgba(27,59,95,0.1)':'rgba(212,175,55,0.15)', color: isA?'#1B3B5F':'#8B6914' }}>Tower {tower}</span>;
};

// ── Horizontal Bar Row ────────────────────────────────────────────────────────
const BarRow = ({ label, value, max, color }) => (
  <div style={{ marginBottom:14 }}>
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:12.5, marginBottom:5 }}>
      <span style={{ fontWeight:600, color:'#2C2C2C' }}>{label}</span>
      <span style={{ color:'#6B6B6B', fontWeight:600 }}>{value}</span>
    </div>
    <div style={{ background:'#E8E4DF', borderRadius:6, height:10, overflow:'hidden' }}>
      <div style={{ width:`${max>0?(value/max)*100:0}%`, background:color, height:'100%', borderRadius:6, transition:'width 0.6s ease' }}/>
    </div>
  </div>
);

// ── Single-line SVG Chart (Admissions only — green) ───────────────────────────
const AdmLineChart = ({ data }) => {
  const W=700, H=220, PL=10, PR=10, PT=20, PB=36;
  const cW=W-PL-PR, cH=H-PT-PB;
  if (!data?.length) return <div style={{ height:H, display:'flex', alignItems:'center', justifyContent:'center', color:'#C4BEB8', fontSize:14 }}>No data</div>;
  const vals = data.map(d=>d.value||0);
  const maxV = Math.max(1,...vals);
  const xOf  = i => PL + (i/Math.max(1,data.length-1))*cW;
  const yOf  = v => PT + cH - (v/maxV)*cH;

  const pts = vals.map((v,i)=>({ x:xOf(i), y:yOf(v) }));
  // Smooth cubic bezier path
  let lineD = `M ${pts[0].x} ${pts[0].y}`;
  for (let i=1;i<pts.length;i++) {
    const mx=(pts[i-1].x+pts[i].x)/2;
    lineD += ` C ${mx} ${pts[i-1].y}, ${mx} ${pts[i].y}, ${pts[i].x} ${pts[i].y}`;
  }
  const areaD = lineD + ` L ${pts[pts.length-1].x} ${PT+cH} L ${pts[0].x} ${PT+cH} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:H }}>
      <defs>
        <linearGradient id="admAreaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#2D5F4F" stopOpacity="0.2"/>
          <stop offset="100%" stopColor="#2D5F4F" stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      {/* Grid lines */}
      {[0,0.25,0.5,0.75,1].map((t,i)=>(
        <line key={i} x1={PL} y1={PT+cH-t*cH} x2={W-PR} y2={PT+cH-t*cH}
          stroke="#E8E4DF" strokeWidth={i===0?1.5:1} strokeDasharray={i===0?'none':'4,4'}/>
      ))}
      {/* Area fill */}
      <path d={areaD} fill="url(#admAreaGrad)"/>
      {/* Line */}
      <path d={lineD} fill="none" stroke="#2D5F4F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Dots */}
      {pts.map((p,i)=>(
        <circle key={i} cx={p.x} cy={p.y} r="4" fill="#2D5F4F" stroke="white" strokeWidth="2"/>
      ))}
      {/* X axis labels */}
      {data.map((d,i)=>(
        <text key={i} x={xOf(i)} y={H-6} textAnchor="middle" fontSize="10" fill="#9B9B9B" fontFamily="Work Sans,sans-serif">{d.label}</text>
      ))}
    </svg>
  );
};

// ── Room type color map ───────────────────────────────────────────────────────
const RT_COLORS = { '4 Sharing':'#1B3B5F','3 Sharing':'#2D5F4F','Twin':'#3a6d97','Premium':'#8B6914','Single':'#c0392b' };

const getRoomTypeLabel = (rt) => {
  const map = { '4sharing':'4 Sharing','4Sharing':'4 Sharing','3sharing':'3 Sharing','3Sharing':'3 Sharing','2sharing':'Twin','2Sharing':'Twin','2sharingPremium':'Premium','private':'Single','1sharing':'Single' };
  return map[rt] || rt || '—';
};

const getTower = (adm) => {
  // ✅ Try all possible sources in priority order
  // bedCode: "AF-73" → first char = A
  // roomBlock: "AF" → first char = A  
  // admissionNumber sometimes encodes tower
  const sources = [adm.bedCode, adm.roomBlock, adm.hostelBlock];
  for (const code of sources) {
    if (code && typeof code === 'string' && code.trim()) {
      const first = code.trim().charAt(0).toUpperCase();
      if (['A','B','C','D','E','F'].includes(first)) return first;
    }
  }
  return '—';
};

const getAdmStatus = (adm) => {
  const now  = new Date();
  const fromStr = adm.dateOfAdmission || '';
  const toStr   = adm.validTill || adm.validityDate || '';
  const from = fromStr ? new Date(fromStr) : null;
  const to   = toStr   ? new Date(toStr)   : null;
  if (!from && !to) return 'UPCOMING';
  if (to && to < now)   return 'EXPIRED';
  if (from && from > now) return 'UPCOMING';
  return 'ACTIVE';
};

const getDuration = (adm) => {
  if (adm.months) return `${adm.months} mo`;
  const from = adm.dateOfAdmission ? new Date(adm.dateOfAdmission) : null;
  const to   = (adm.validTill || adm.validityDate) ? new Date(adm.validTill || adm.validityDate) : null;
  if (!from || !to) return '—';
  return `${Math.round((to-from)/(1000*60*60*24*30))} mo`;
};

// ════════════════════════════════════════════════════════════════════════════
const AdmissionReport = () => {
  const [admissions,  setAdmissions]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [period,      setPeriod]      = useState('week');
  const [dateOffset,  setOffset]      = useState(0);
  const [fStatus,     setFStatus]     = useState('all');
  const [fTower,      setFTower]      = useState('all');
  const [fRoomType,   setFRoomType]   = useState('all');
  const [fYear,       setFYear]       = useState('all');
  const [search,      setSearch]      = useState('');
  const [sort,        setSort]        = useState({ col:'admdate', dir:'desc' });
  const [page,        setPage]        = useState(1);

  useEffect(() => {
    setLoading(true);
    api.get('/admissions')
      .then(res => {
          const d=res.data;
          const list = Array.isArray(d)?d:(d?.content||[]);
          // ✅ Debug: log first record to check field names
          if (list.length > 0) console.log('[AdmissionReport] Sample record:', JSON.stringify(list[0], null, 2));
          setAdmissions(list);
        })
      .catch(()=>setAdmissions([]))
      .finally(()=>setLoading(false));
  }, []);

  // ── Period date range + label ───────────────────────────────────────────────
  const { rangeFrom, rangeTo, periodLabel } = useMemo(() => {
    const now  = new Date();
    const nowY = now.getFullYear();
    const nowM = now.getMonth();
    const nowD = now.getDate();
    const fmt  = (y,m,d) => `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    const today = fmt(nowY, nowM, nowD);
    const lastDay = (y,m) => new Date(y,m+1,0).getDate();

    if (period === 'day') {
      const d = new Date(nowY, nowM, nowD + dateOffset);
      const ds = fmt(d.getFullYear(), d.getMonth(), d.getDate());
      const to = ds > today ? today : ds;
      return {
        rangeFrom: ds,
        rangeTo:   to,
        periodLabel: d.toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})
      };
    }
    if (period === 'week') {
      const mon = new Date(nowY, nowM, nowD - ((now.getDay()||7)-1) + dateOffset*7);
      const sun = new Date(mon); sun.setDate(sun.getDate()+6);
      const from = fmt(mon.getFullYear(), mon.getMonth(), mon.getDate());
      const to   = fmt(sun.getFullYear(), sun.getMonth(), sun.getDate());
      return {
        rangeFrom: from,
        rangeTo:   to > today ? today : to,
        periodLabel: `${mon.toLocaleDateString('en-IN',{day:'numeric',month:'short'})} – ${sun.toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}`
      };
    }
    // month
    const d  = new Date(nowY, nowM + dateOffset, 1);
    const y  = d.getFullYear(), m = d.getMonth();
    const to = fmt(y, m, lastDay(y,m));
    return {
      rangeFrom: fmt(y, m, 1),
      rangeTo:   to > today ? today : to,
      periodLabel: d.toLocaleDateString('en-IN',{month:'long',year:'numeric'})
    };
  }, [period, dateOffset]);

  // ── Enrich admissions ───────────────────────────────────────────────────────
  const enriched = useMemo(() => admissions.map(a => ({
    ...a,
    _status:   getAdmStatus(a),
    _tower:    getTower(a),
    _roomType: getRoomTypeLabel(a.roomType),
    _duration: getDuration(a),
    _fees:     Number(a.totalFees||0),
  })), [admissions]);

  // ── Stats — filtered by period ──────────────────────────────────────────────
  const periodEnriched = useMemo(() => enriched.filter(a => {
    const admDate = a.dateOfAdmission || '';
    if (admDate && rangeFrom && rangeTo) {
      return admDate >= rangeFrom && admDate <= rangeTo;
    }
    return true;
  }), [enriched, rangeFrom, rangeTo]);

  const stats = useMemo(() => ({
    total:    periodEnriched.length,
    active:   periodEnriched.filter(a=>a._status==='ACTIVE').length,
    upcoming: periodEnriched.filter(a=>a._status==='UPCOMING').length,
    expired:  periodEnriched.filter(a=>a._status==='EXPIRED').length,
    fees:     periodEnriched.reduce((s,a)=>s+a._fees,0),
  }), [periodEnriched]);

  // ── Last 7 days chart ───────────────────────────────────────────────────────
  const chartData = useMemo(() => {
    // For day: show hourly; for week: show daily; for month: show daily
    if (period === 'day') {
      return Array.from({length:24},(_,h)=>({
        label: `${h}:00`,
        value: periodEnriched.filter(a=>{
          if (!a.createdAt) return false;
          return new Date(a.createdAt).getHours()===h;
        }).length
      })).filter((_,h)=>h>=6&&h<=22);
    }
    if (period === 'week') {
      const mon = new Date(); mon.setDate(mon.getDate()-((mon.getDay()||7)-1)+dateOffset*7);
      return Array.from({length:7},(_,i)=>{
        const d = new Date(mon); d.setDate(d.getDate()+i);
        const key = d.toISOString().slice(0,10);
        return {
          label: d.toLocaleDateString('en-IN',{weekday:'short'}).slice(0,3),
          value: periodEnriched.filter(a=>(a.dateOfAdmission||'').startsWith(key)).length
        };
      });
    }
    // month — daily breakdown
    const from = new Date(rangeFrom), to = new Date(rangeTo);
    const days = [];
    for (let d=new Date(from); d<=to; d.setDate(d.getDate()+1)) {
      const key = d.toISOString().slice(0,10);
      days.push({
        label: String(d.getDate()),
        value: periodEnriched.filter(a=>(a.dateOfAdmission||'').startsWith(key)).length
      });
    }
    return days;
  }, [periodEnriched, period, dateOffset, rangeFrom, rangeTo]);

  // ── Tower split ─────────────────────────────────────────────────────────────
  const towerSplit = useMemo(() => {
    const m={}; periodEnriched.forEach(a=>{ if(a._tower!=='—') m[a._tower]=(m[a._tower]||0)+1; });
    return Object.entries(m).sort((a,b)=>b[1]-a[1]);
  }, [periodEnriched]);

  // ── Room type split ─────────────────────────────────────────────────────────
  const roomSplit = useMemo(() => {
    const m={}; periodEnriched.forEach(a=>{ if(a._roomType!=='—') m[a._roomType]=(m[a._roomType]||0)+1; });
    return Object.entries(m).sort((a,b)=>b[1]-a[1]);
  }, [periodEnriched]);

  // ── Filtered + sorted ───────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return enriched.filter(a => {
      // ✅ Period filter — filter by admission date within selected range
      const admDate = a.dateOfAdmission || '';
      if (admDate && rangeFrom && rangeTo) {
        if (admDate < rangeFrom || admDate > rangeTo) return false;
      }
      if (fStatus!=='all' && a._status!==fStatus.toUpperCase()) return false;
      if (fTower!=='all'  && a._tower!==fTower) return false;
      if (fRoomType!=='all' && a._roomType!==fRoomType) return false;
      if (fYear!=='all' && !(a.dateOfAdmission||'').includes(fYear)) return false;
      const q=search.toLowerCase();
      if (q && !a.fullName?.toLowerCase().includes(q) && !a.admissionNumber?.toLowerCase().includes(q)) return false;
      return true;
    }).sort((a,b) => {
      const dir = sort.dir==='asc'?1:-1;
      if (sort.col==='name')     return dir*(a.fullName||'').localeCompare(b.fullName||'');
      if (sort.col==='sid')      return dir*(a.admissionNumber||'').localeCompare(b.admissionNumber||'');
      if (sort.col==='tower')    return dir*a._tower.localeCompare(b._tower);
      if (sort.col==='roomtype') return dir*a._roomType.localeCompare(b._roomType);
      if (sort.col==='status')   return dir*a._status.localeCompare(b._status);
      if (sort.col==='fees')     return dir*(a._fees-b._fees);
      if (sort.col==='to')       return dir*(a.validTill||a.validityDate||'').localeCompare(b.validTill||b.validityDate||'');
      return dir*(a.dateOfAdmission||'').localeCompare(b.dateOfAdmission||'');
    });
  }, [enriched, fStatus, fTower, fRoomType, fYear, search, sort, rangeFrom, rangeTo]);

  const totalPages = Math.ceil(filtered.length/PAGE_SIZE);
  const pageData   = filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);
  const sortBy     = col => { setSort(s=>({col, dir:s.col===col&&s.dir==='asc'?'desc':'asc'})); setPage(1); };

  // ── Export ──────────────────────────────────────────────────────────────────
  const exportXLSX = useCallback(() => {
    import('xlsx').then(XLSX => {
      const summary = [
        ['Omkara Hostel — Admissions Report'],
        ['Generated', new Date().toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})],
        [],['SUMMARY'],
        ['Total Admissions', stats.total],['Active', stats.active],['Upcoming', stats.upcoming],
        ['Expired', stats.expired],['Fees Committed', `₹${stats.fees.toLocaleString('en-IN')}`],
      ];
      const headers = ['Student ID','Student Name','Admission Date','Tower','Room Type','From','To','Duration','Total Fees (₹)','Status'];
      const rows    = filtered.map(a=>[a.admissionNumber||'',a.fullName||'',fmtDate(a.dateOfAdmission),`Tower ${a._tower}`,a._roomType,fmtDate(a.dateOfAdmission),fmtDate(a.validTill||a.validityDate),a._duration,a._fees,a._status]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([headers,...rows]), 'Admission Records');
      XLSX.writeFile(wb,'Omkara_Admissions_Report.xlsx');
    });
  }, [filtered, stats]);

  // ── Common styles ───────────────────────────────────────────────────────────
  const sel  = { padding:'9px 14px', borderRadius:7, border:'1.5px solid #E8E4DF', fontFamily:'Work Sans,sans-serif', fontSize:12.5, color:'#2C2C2C', background:'#fff', cursor:'pointer', outline:'none' };
  const tab  = a => ({ padding:'8px 18px', borderRadius:6, fontSize:12, fontWeight:600, letterSpacing:0.8, textTransform:'uppercase', cursor:'pointer', fontFamily:'Work Sans,sans-serif', border:'none', background:a?'#1B3B5F':'transparent', color:a?'#fff':'#6B6B6B', transition:'all 0.2s' });
  const th   = { padding:'13px 20px', textAlign:'left', fontSize:10, letterSpacing:1.5, textTransform:'uppercase', color:'#6B6B6B', fontWeight:700, borderBottom:'1px solid #E8E4DF', whiteSpace:'nowrap', cursor:'pointer', background:'#F8F6F3' };
  const td   = { padding:'14px 20px', fontSize:13.5, borderBottom:'1px solid #E8E4DF', color:'#2C2C2C', verticalAlign:'middle' };
  const btnG = { padding:'9px 20px', border:'none', borderRadius:6, fontFamily:'Work Sans,sans-serif', fontWeight:600, fontSize:12, textTransform:'uppercase', letterSpacing:1, cursor:'pointer', display:'flex', alignItems:'center', gap:7, background:'#D4AF37', color:'#1B3B5F' };
  const btnO = { ...btnG, background:'transparent', color:'#1B3B5F', border:'1.5px solid #E8E4DF' };

  const maxTower = towerSplit.length ? Math.max(...towerSplit.map(([,v])=>v)) : 1;
  const maxRoom  = roomSplit.length  ? Math.max(...roomSplit.map(([,v])=>v))  : 1;

  return (
    <div>

      {/* ══ CONTROLS BAR ══════════════════════════════════════════════════════ */}
      <div style={{ background:'#fff', borderRadius:12, padding:'24px 32px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)', marginBottom:24, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:20 }}>
        <div>
          <h2 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:26, fontWeight:500, color:'#1B3B5F', marginBottom:4 }}>Admissions Report</h2>
          <p style={{ fontSize:13, color:'#6B6B6B' }}>Showing data for {periodLabel}</p>
        </div>
        <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
          {/* Period tabs */}
          <div style={{ display:'flex', background:'#F8F6F3', borderRadius:8, padding:4, gap:2 }}>
            {['day','week','month'].map(p=>(
              <button key={p} style={tab(period===p)} onClick={()=>{ setPeriod(p); setOffset(0); }}>
                {p.charAt(0).toUpperCase()+p.slice(1)}
              </button>
            ))}
          </div>
          {/* Date nav */}
          <div style={{ display:'flex', alignItems:'center', gap:10, background:'#fff', border:'1.5px solid #E8E4DF', borderRadius:8, padding:'6px 14px' }}>
            <button onClick={()=>setOffset(o=>o-1)} style={{ width:28, height:28, borderRadius:6, border:'none', background:'#F8F6F3', cursor:'pointer', fontSize:14, color:'#1B3B5F' }}>‹</button>
            <span style={{ fontSize:13, fontWeight:600, color:'#1B3B5F', minWidth:160, textAlign:'center' }}>{periodLabel}</span>
            <button onClick={()=>setOffset(o=>o+1)} style={{ width:28, height:28, borderRadius:6, border:'none', background:'#F8F6F3', cursor:'pointer', fontSize:14, color:'#1B3B5F' }}>›</button>
          </div>
        </div>
      </div>

      {/* ══ FILTERS ══════════════════════════════════════════════════════════ */}
      <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap', marginBottom:22 }}>
        <span style={{ fontSize:11, letterSpacing:1.5, textTransform:'uppercase', color:'#6B6B6B', fontWeight:600 }}>Filter by:</span>
        <select style={sel} value={fStatus} onChange={e=>{ setFStatus(e.target.value); setPage(1); }}>
          <option value="all">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="UPCOMING">Upcoming</option>
          <option value="EXPIRED">Expired</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <select style={sel} value={fTower} onChange={e=>{ setFTower(e.target.value); setPage(1); }}>
          <option value="all">All Towers</option>
          <option value="A">Tower A</option>
          <option value="B">Tower B</option>
        </select>
        <select style={sel} value={fRoomType} onChange={e=>{ setFRoomType(e.target.value); setPage(1); }}>
          <option value="all">All Room Types</option>
          <option value="4 Sharing">4 Sharing</option>
          <option value="3 Sharing">3 Sharing</option>
          <option value="Twin">Twin (2 Sharing)</option>
          <option value="Premium">Premium (2 Sharing)</option>
          <option value="Single">Single (Private)</option>
        </select>
        <select style={sel} value={fYear} onChange={e=>{ setFYear(e.target.value); setPage(1); }}>
          <option value="all">All Years</option>
          {[2024,2025,2026].map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <button style={{ ...btnO, marginLeft:'auto', padding:'8px 16px', fontSize:11 }} onClick={()=>{ setFStatus('all'); setFTower('all'); setFRoomType('all'); setFYear('all'); setSearch(''); setPage(1); }}>✕ Clear</button>
      </div>

      {/* ══ SUMMARY CARDS — 5 cards specific to admissions ═══════════════════ */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:16, marginBottom:24 }}>
        {[
          { label:'Total Admissions', value:stats.total,           color:'#1B3B5F', sub:'All time',           bold:true  },
          { label:'Active',           value:stats.active,          color:'#2D5F4F', sub:'Currently in stay'             },
          { label:'Upcoming',         value:stats.upcoming,        color:'#3a6d97', sub:'Stay not yet started'          },
          { label:'Expired',          value:stats.expired,         color:'#C4BEB8', sub:'Stay period ended'             },
          { label:'Fees Committed',   value:fmtMoney(stats.fees),  color:'#D4AF37', sub:'Total for period',   isMoney:true },
        ].map(c=>(
          <div key={c.label} style={{ background:'#fff', borderRadius:10, padding:'20px 22px', boxShadow:'0 2px 10px rgba(0,0,0,0.05)', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, width:4, height:'100%', background:c.color }}/>
            <div style={{ fontSize:10, letterSpacing:1.5, textTransform:'uppercase', color:'#6B6B6B', marginBottom:10, fontWeight:600 }}>{c.label}</div>
            <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:c.isMoney?22:32, fontWeight:600, color:'#1B3B5F', lineHeight:1 }}>
              {loading ? '…' : c.value}
            </div>
            <div style={{ fontSize:11, color:'#9B9B9B', marginTop:6 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* ══ ADMISSIONS TREND CHART — single green line (unlike registration) ═ */}
      <div style={{ background:'#fff', borderRadius:12, padding:'24px 28px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)', marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, fontWeight:600, color:'#1B3B5F' }}>
            Admissions Trend
            <span style={{ fontFamily:'Work Sans,sans-serif', fontSize:12, fontWeight:400, color:'#6B6B6B', marginLeft:8 }}>— Last 7 Days</span>
          </div>
          {/* Single legend: Admissions only */}
          <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#6B6B6B' }}>
            <svg width="24" height="10"><line x1="0" y1="5" x2="24" y2="5" stroke="#2D5F4F" strokeWidth="2.5" strokeLinecap="round"/><circle cx="12" cy="5" r="3" fill="#2D5F4F"/></svg>
            Admissions
          </div>
        </div>
        {loading
          ? <div style={{ height:220, display:'flex', alignItems:'center', justifyContent:'center', color:'#C4BEB8' }}>Loading…</div>
          : <AdmLineChart data={chartData}/>
        }
      </div>

      {/* ══ TWO BAR CHARTS SIDE BY SIDE — unique to admission report ══════════ */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>

        {/* Tower Split */}
        <div style={{ background:'#fff', borderRadius:12, padding:'24px 28px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, fontWeight:600, color:'#1B3B5F', marginBottom:18 }}>Tower Split</div>
          {loading
            ? <div style={{ color:'#C4BEB8', fontSize:13 }}>Loading…</div>
            : towerSplit.length===0
              ? <div style={{ color:'#C4BEB8', fontSize:13, textAlign:'center', padding:'20px 0' }}>No data yet</div>
              : towerSplit.map(([t,v])=>(
                  <BarRow key={t} label={`Tower ${t}`} value={v} max={maxTower} color={t==='A'?'#1B3B5F':'#D4AF37'}/>
                ))
          }
        </div>

        {/* Room Type Breakdown */}
        <div style={{ background:'#fff', borderRadius:12, padding:'24px 28px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, fontWeight:600, color:'#1B3B5F', marginBottom:18 }}>Room Type Breakdown</div>
          {loading
            ? <div style={{ color:'#C4BEB8', fontSize:13 }}>Loading…</div>
            : roomSplit.length===0
              ? <div style={{ color:'#C4BEB8', fontSize:13, textAlign:'center', padding:'20px 0' }}>No data yet</div>
              : roomSplit.map(([rt,v])=>(
                  <BarRow key={rt} label={rt} value={v} max={maxRoom} color={RT_COLORS[rt]||'#1B3B5F'}/>
                ))
          }
        </div>
      </div>

      {/* ══ TABLE — different columns from registration ════════════════════════ */}
      <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', overflow:'hidden' }}>
        {/* Toolbar */}
        <div style={{ padding:'18px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid #E8E4DF', flexWrap:'wrap', gap:12 }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, fontWeight:600, color:'#1B3B5F' }}>Admission Records</div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, background:'#F8F6F3', borderRadius:8, padding:'8px 14px' }}>
              <span style={{ color:'#6B6B6B', fontSize:14 }}>🔍</span>
              <input placeholder="Search by name or ID…" value={search}
                onChange={e=>{ setSearch(e.target.value); setPage(1); }}
                style={{ border:'none', background:'transparent', fontFamily:'Work Sans,sans-serif', fontSize:13, color:'#2C2C2C', outline:'none', width:180 }}/>
            </div>
            <button style={btnG} onClick={exportXLSX}>⬇ Export XLSX</button>
          </div>
        </div>

        {/* Table */}
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              {[['sid','Student ID'],['name','Student Name'],['admdate','Admission Date'],['tower','Tower'],['roomtype','Room Type'],['admdate','From'],['to','To'],['duration','Duration'],['fees','Total Fees'],['status','Status']].map(([col,label],i)=>(
                <th key={i} style={th} onClick={()=>sortBy(col)}>
                  {label} {sort.col===col?(sort.dir==='asc'?'↑':'↓'):'↕'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ padding:'60px 0', textAlign:'center', color:'#C4BEB8', fontSize:14 }}>Loading…</td></tr>
            ) : pageData.length===0 ? (
              <tr><td colSpan={10} style={{ padding:'60px 0', textAlign:'center' }}>
                <div style={{ fontSize:44, opacity:0.3, marginBottom:14 }}>🔍</div>
                <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, color:'#6B6B6B', marginBottom:6 }}>No records found</div>
                <div style={{ fontSize:13, color:'#C4BEB8' }}>Try adjusting your filters.</div>
              </td></tr>
            ) : pageData.map((a,i)=>(
              <tr key={a.id||i}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(27,59,95,0.02)'}
                onMouseLeave={e=>e.currentTarget.style.background=''}>
                <td style={{ ...td, fontFamily:'monospace', fontSize:12.5, color:'#6B6B6B' }}>{a.admissionNumber||'—'}</td>
                <td style={{ ...td, fontWeight:600, color:'#1B3B5F' }}>{a.fullName||'—'}</td>
                <td style={td}>{fmtDate(a.dateOfAdmission)}</td>
                <td style={td}><TowerPill tower={a._tower}/></td>
                <td style={{ ...td, fontSize:12, fontWeight:600, color:RT_COLORS[a._roomType]||'#2C2C2C' }}>{a._roomType}</td>
                <td style={td}>{fmtDate(a.dateOfAdmission)}</td>
                <td style={td}>{fmtDate(a.validTill||a.validityDate)}</td>
                <td style={{ ...td, fontSize:12, color:'#6B6B6B' }}>{a._duration}</td>
                <td style={{ ...td, fontWeight:600, color:'#1B3B5F' }}>₹{a._fees.toLocaleString('en-IN')}</td>
                <td style={td}><StatusPill status={a._status}/></td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div style={{ padding:'13px 22px', display:'flex', alignItems:'center', justifyContent:'space-between', borderTop:'1px solid #E8E4DF', background:'#F8F6F3' }}>
          <span style={{ fontSize:12, color:'#6B6B6B' }}>
            Showing {filtered.length===0?0:(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE,filtered.length)} of {filtered.length} records
          </span>
          <div style={{ display:'flex', gap:4 }}>
            {[{label:'‹',p:page-1},...Array.from({length:Math.min(totalPages,5)},(_,i)=>({label:String(i+1),p:i+1})),{label:'›',p:page+1}].map(({label,p:tp},idx)=>(
              <button key={idx}
                onClick={()=>{ if(tp>=1&&tp<=totalPages) setPage(tp); }}
                disabled={(label==='‹'&&page===1)||(label==='›'&&page===totalPages)}
                style={{ width:32, height:32, borderRadius:6, border:`1.5px solid ${String(tp)===String(page)?'#1B3B5F':'#E8E4DF'}`, background:String(tp)===String(page)?'#1B3B5F':'#fff', color:String(tp)===String(page)?'#fff':'#6B6B6B', fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Work Sans,sans-serif', opacity:((label==='‹'&&page===1)||(label==='›'&&page===totalPages))?0.4:1 }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdmissionReport;